import { Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { UtilsService } from 'core/utils/utils.service';
import { PriceAsService } from 'src/app/modules/marketing/products/services/price-as-service/price-as.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { QuoteDetails } from 'src/app/modules/shared/entities/quote-details.class';
import { Lead } from 'src/app/modules/crm/leads/entities/lead.class';
import { QuoteTemplateValues } from 'src/app/modules/shared/entities/quote-template-values.class';
import { AccountManager } from '../../../shared/entities/account-manager.class';
import { CustomerType } from '../../../shared/entities/customer-type.enum';
import { Customer } from '../../../shared/entities/customer.class';
import { SignalrConnectionType } from '../../../shared/entities/signalrConnectionType.enum';
import { SignalrMethodName } from '../../../shared/entities/signalrMethodName.enum';
import { SignalrNotification } from '../../../shared/entities/SignalrNotification.class';
import { SignalrProgressState } from '../../../shared/entities/signalrProgressState.enum';
import { DefaultRoute } from '../entities/default-route.class';
import { FederalPriorityCode } from '../entities/federal-code.class';
import { PriceMatrix } from '../entities/price-matrix.class';
import { ConsignedWarehouseService } from '../services/consigned-warehouse.service';
import { PaymentDetailsService } from '../services/payment-details.service';
import { QuoteDetailsService } from '../services/quote-details.service';
import { QuoteToolsService } from '../services/quote-tools.service';

@Component({
  selector: 'app-quote-details',
  templateUrl: './quote-details.component.html',
  styleUrls: ['./quote-details.component.scss'],
  standalone: false
})
export class QuoteDetailsComponent implements OnInit, OnDestroy {
  appStrings: AppLocalization;
  dsDateFormat: string;
  isMobile: boolean;
  dialogButtons: any;
  dialogTitle: string;
  dialogText: any;
  dialogOpen$ = new EventEmitter<boolean>();
  quoteDetailsForm: FormGroup;
  federalPriorityCodeList: FederalPriorityCode[];
  displayFederalPriorityCodeList: { value: string, label: string }[] = [];
  leadStatusCodeList: any;
  lnStatusCodeList: any;
  accountManagerList: AccountManager[];
  displayAccountManagerList: { value: string, label: string }[] = [];
  currentAccountManager: AccountManager;
  currentCustomer: Customer | Lead;
  currentPriceMatrix: PriceMatrix;
  customerList: Customer[];
  displayCustomerList: { value: string, label: string }[] = [];
  priceMatrixList: PriceMatrix[];
  displayPriceMatrixList: { value: string, label: string }[] = [];
  defaultRouteList: DefaultRoute[];
  displayDefaultRouteList: { value: string, label: string }[] = [];
  displayPriceAsList: { value: string, label: string }[] = [];
  customerId: string;
  isLead = false;
  leadId: string;
  quoteId: string;
  isAdminTypeUser: boolean;
  isFederalCustomer: boolean;
  lead: any;
  disableCustomerField = true;
  quoteIsCompleted = false;
  quoteSaveInProgress = false;
  quoteSaveNotificationTimeout: ReturnType<typeof setTimeout> = null;
  delayForNotification = 700;
  isNotificationShown = false;
  private subs = new Subscription();
  hasItemsFromCW;
  private productCount;
  private disableAccountManagerRestore = false;
  oldPriceMatrix;
  isOnline: boolean;
  electronAppOutOfSync = false;
  currentSelectedCarrier;
  priorityLabel = '1NRSH';
  publicListConst = 'Public List';
  industrialListConst = 'Industrial List';
  priceAsList: Customer[] = [];
  currentPriceAs: Customer = null;
  fromPriceAs = false;
  hideInactive = true;
  highValueQuoteLimit = 50000;
  private currentSubTotal = 0;

  @Output() goToProductList = new EventEmitter();
  @Output() displayQuoteIsExpired = new EventEmitter();
  @Output() displayPriceMatrixChangedAfterMerge = new EventEmitter();
  @Output() handleMigratedQuote = new EventEmitter();
  @Output() hideInactiveChange = new EventEmitter<boolean>();

  unalteredPriceMatrixList: any;
  countryCode: string;
  minDate: Date;
  maxDate: Date;
  today: Date;
  displayQuoteCalendarExpirationDate: boolean;
  selectedExpirationDate: Date;
  priceAsEnabled = false;
  confidenceOptions = {};
  confidenceOptionsValues = [];
  displayConfidenceOptionsValues: { value: string, label: string }[] = [];
  displayEstimatedDate: boolean;

  constructor(
    public activatedRoute: ActivatedRoute,
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private quoteDetailsService: QuoteDetailsService,
    private fb: FormBuilder,
    private quoteToolService: QuoteToolsService,
    private consignedWarehouseService: ConsignedWarehouseService,
    private paymentDetailsService: PaymentDetailsService,
    private signalrService: SignalRService,
    private notificationService: NotificationService,
    private location: Location,
    private priceAsService: PriceAsService,
    private utilsService: UtilsService,
    private deviceService: DeviceDetectorService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.today = new Date();
    this.minDate = new Date();
    this.minDate.setDate(this.today.getDate() + 1);
    this.isOnline = this.appStateService.state.isOnline;
    this.electronAppOutOfSync =
      this.appStateService.checkIfElectronAppIsOutOfSync();
    this.countryCode = this.appInfoService.userData.UserInfo.CountryCode;
    this.initData();
    this.subs.add(
      this.activatedRoute.params.subscribe(() => this.initDataForQuote())
    );
    this.subs.add(
      this.quoteToolService.hasProductsFromConsigned$.subscribe(
        (hasItems) => (this.hasItemsFromCW = hasItems)
      )
    );
    this.subs.add(
      this.quoteToolService.productCount$.subscribe(
        (productsNumber) => (this.productCount = productsNumber)
      )
    );
    this.subs.add(
      this.signalrService.editQuoteForCalculateTotalsProgressReceived$.subscribe(
        (editQuoteProgress) => this.setEditQuoteProgress(editQuoteProgress)
      )
    );
    this.subs.add(
      this.quoteDetailsService.quoteIsSaving$.subscribe((progress) =>
        this.updateStatusForQuoteSavingProgress(progress)
      )
    );
    this.subs.add(
      this.quoteDetailsService.selectedCarrier$.subscribe((selectedCarrier) =>
        this.updateSelectedCarrier(selectedCarrier)
      )
    );
    this.subs.add(
      this.quoteToolService.priceMatrixListRefresh$.subscribe(() =>
        this.filterPriceMatrixes()
      )
    );
    this.subs.add(
      this.paymentDetailsService.shipToCompleteChecked$.subscribe((value) => {
        this.quoteDetailsForm.controls.shipToComplete.setValue(value);
        this.processShipToCompleteValue(true);
      })
    );
    this.subs.add(
      this.paymentDetailsService.userAttributes$.subscribe(result => {
        if (result.hideInactiveCustomers !== this.hideInactive) {
          this.hideInactive = result.hideInactiveCustomers;
          this.quoteDetailsForm.controls.hideInactiveCustomers.patchValue(this.hideInactive);
          if (this.currentAccountManager) {
            this.quoteDetailsService.getCustomersAndLeads(
              this.currentAccountManager.employeeId, this.hideInactive,
              (customers: Customer[]) => this.setCustomers(customers)
            );
          }
        } else {
          this.quoteDetailsForm.controls.hideInactiveCustomers.patchValue(this.hideInactive);
        }
      })
    );

    this.subs.add(
      this.paymentDetailsService.paymentSummary$.subscribe(paymentSummary => {
        this.currentSubTotal = paymentSummary?.subTotal || 0;
        this.quoteDetailsForm.controls.confidenceToClose.updateValueAndValidity();
        this.quoteDetailsForm.controls.estimatedCloseDate.updateValueAndValidity();
      })
    );

    this.subs.add(
      this.quoteDetailsForm.controls.priceAs.valueChanges.subscribe(value => {
        if ((!value || value === '') && this.currentCustomer?.isLead && this.currentPriceAs) {
          this.currentPriceAs = null;
          this.resetPriceMatrixToDefault();
        }
      })
    );
  }

  hasExpirationOption() {
    if (
      (this.countryCode === 'GB' || this.countryCode === 'DE') &&
      this.maxDate
    ) {
      this.displayQuoteCalendarExpirationDate = true;
    } else {
      this.displayQuoteCalendarExpirationDate = false;
    }
  }

  private initData() {
    this.appStrings = this.appInfoService.appStrings;
    this.dsDateFormat = this.appInfoService.dsDateFormat;
    this.isAdminTypeUser = this.appInfoService.isAdminTypeUser();
    this.priceAsEnabled =
      this.countryCode === 'US' || this.countryCode === 'CA';
    this.quoteDetailsForm = this.fb.group({
      id: [this.appInfoService.emptyGuid], // guid
      quoteNumber: [''], // string
      accountManager: ['', Validators.required], // guid
      customer: ['', Validators.required], // guid
      federalPriorityCode: [null], // number
      priceMatrix: [null, Validators.required], // id: number
      defaultRoute: [null], // guid
      quoteNotes: ['', Validators.maxLength(2000)],
      quoteDate: [''], // ISO string
      shipToComplete: false,
      quoteName: ['', Validators.maxLength(20)], // string
      confidenceToClose: [null, this.validateConfidenceToClose.bind(this)],
      estimatedCloseDate: [null, this.validateEstimatedCloseDate.bind(this)],
      quoteExpireDate: this.selectedExpirationDate,
      priceAs: [''], // guid
      hideInactiveCustomers: false
    });
  }

  /**
   * Custom validator for confidenceToClose field
   */
  private validateConfidenceToClose(control: any) {
    if (!this.isLead && this.currentSubTotal >= this.highValueQuoteLimit) {
      const controlValue = control.value;
      const isTargetConfidence = controlValue === '1';

      if (isTargetConfidence) {
        return { required: true };
      }
    }

    return null;
  }

  /**
   * Custom validator for estimatedCloseDate field
   */
  private validateEstimatedCloseDate(control: any) {
    if (this.displayEstimatedDate && this.currentSubTotal >= this.highValueQuoteLimit && !control.value) {
      return { required: true };
    }

    return null;
  }

  private initDataForQuote() {
    this.quoteId = this.activatedRoute.snapshot.params['quoteId'];
    this.customerId = this.activatedRoute.snapshot.params['customerId'];
    this.leadId = this.activatedRoute.snapshot.params['leadId'];
    this.fromPriceAs = this.activatedRoute.snapshot.data?.fromPriceAs && Boolean(this.priceAsService.quoteInfo);
    this.isFederalCustomer = false;
    this.quoteDetailsService.getStaticValues(
      (staticValues: [QuoteTemplateValues, AccountManager[], DefaultRoute[], PriceMatrix[]]) => {
        this.initializeValues(staticValues);
        this.initializeQuoteValues();
      });
  }

  /**
   * Initializes template, account managers and default routes values
   */
  private initializeValues([
    templateValues,
    accountManagers,
    defaultRoutes,
    priceMatrixes,
  ]: [QuoteTemplateValues, AccountManager[], DefaultRoute[], PriceMatrix[]]) {
    this.quoteToolService.priceMatrixListRefresh$.next(undefined);
    this.federalPriorityCodeList = this.mapFederalCodes(templateValues);
    this.displayFederalPriorityCodeList = this.utilsService.mapObjectToSelect(
      this.federalPriorityCodeList,
      'code',
      'label'
    );
    this.leadStatusCodeList = templateValues.LeadStatus;
    this.lnStatusCodeList = templateValues.LnStatus;
    this.accountManagerList = accountManagers;
    this.displayAccountManagerList = this.utilsService.mapObjectToSelect(accountManagers, 'employeeId', 'name');
    this.defaultRouteList = defaultRoutes;
    this.updateDefaultRouteList();
    this.unalteredPriceMatrixList = priceMatrixes;
    this.displayPriceMatrixList = this.utilsService.mapStringValueObjectToSelect(priceMatrixes, 'id', 'priceList');

    this.confidenceOptions = templateValues.ConfidenceToClose;
    this.confidenceOptionsValues = Object.values(
      templateValues.ConfidenceToClose
    );
    this.displayConfidenceOptionsValues = this.utilsService.mapKeyValueOptions(templateValues.ConfidenceToClose);
    this.quoteDetailsForm.controls.confidenceToClose.patchValue(Object.keys(templateValues.ConfidenceToClose)[0]);
  }

  updateCloseDate(date: Date) {
    this.quoteDetailsForm.controls.estimatedCloseDate.patchValue(
      new Date(date).toISOString()
    );
  }

  updateConfidence(confidenceValue) {
    const value = this.confidenceOptions[confidenceValue];
    if (value && (value !== this.confidenceOptionsValues[0])) {
      this.displayEstimatedDate = true;
    } else {
      this.displayEstimatedDate = false;
    }
  }

  /**
   * Updates display default route list when defaultRouteList changes
   * Maintains form control value consistency between display and original list
   */
  private updateDefaultRouteList() {
    const currentValue = this.quoteDetailsForm.controls.defaultRoute.value;
    this.displayDefaultRouteList = this.utilsService.mapObjectToSelect(this.defaultRouteList, 'id', 'route');

    if (currentValue && typeof currentValue === 'string') {
      const routeStillExists = this.defaultRouteList.find(route => route.id === currentValue);
      if (!routeStillExists) {
        this.quoteDetailsForm.controls.defaultRoute.setValue(null);
      }
    }
  }

  filterPriceMatrixes() {
    if (this.countryCode === 'US' && this.isLead !== true) {
      this.priceMatrixList = this.unalteredPriceMatrixList?.filter(
        (item) =>
          item.priceList !== this.industrialListConst &&
          item.priceList !== this.publicListConst
      );
    } else {
      if (this.isLead === true && this.countryCode === 'US') {
        this.priceMatrixList = this.unalteredPriceMatrixList?.filter(
          (item) => item.priceList !== this.publicListConst
        );
      } else {
        this.priceMatrixList = this.unalteredPriceMatrixList;
      }
    }
    this.displayPriceMatrixList = this.utilsService.mapStringValueObjectToSelect(this.priceMatrixList, 'id', 'priceList');
  }

  processShipToCompleteValue($event?) {
    const defaultRouteDisabled = this.quoteDetailsForm.controls.shipToComplete.value;
    if (defaultRouteDisabled) {
      // change the default route when available
      const defaultSCOMP = this.quoteDetailsService.getShipToCompleteSCOM(
        this.defaultRouteList
      );
      if (defaultSCOMP) {
        this.quoteDetailsForm.controls.defaultRoute.setValue(defaultSCOMP.id);
      }
      this.quoteDetailsForm.controls.defaultRoute.disable();
    } else {
      if (!this.quoteIsCompleted && !this.electronAppOutOfSync) {
        this.quoteDetailsForm.controls.defaultRoute.enable();
        if ($event) {
          // clicked from HTML, unchecked, revert default route
          this.resetInitialDefaultRoute();
        }
      }
    }
  }

  private setPriorityExpeditedRoute() {
    const route = this.defaultRouteList.find((item) =>
      item.route.includes(this.priorityLabel)
    );
    if (route) {
      this.quoteDetailsForm.controls.defaultRoute.setValue(route.id);
    }
  }

  private resetInitialDefaultRoute() {
    // we get the customer details and set the default route
    const cust = this.currentCustomer as Customer;
    const custType = cust?.isLead ? CustomerType.Lead : CustomerType.Customer;
    if (this.currentSelectedCarrier?.expeditedShipping) {
      this.setPriorityExpeditedRoute();
    } else {
      this.quoteDetailsService.getQuoteDetailsByClientType(
        custType,
        cust.id,
        (result) => this.fillDropDownDefaultRoute(result)
      );
    }
  }

  private updateSelectedCarrier(selectedCarrier) {
    // Should set quote details route to 1NRSH if carrier is next day air, 2nd day air or priority
    if (selectedCarrier.expeditedShipping) {
      this.setPriorityExpeditedRoute();
    } else {
      const route = this.defaultRouteList.find((item) =>
        item.route.includes(this.priorityLabel)
      );

      // if old carrier is expedited, new carrier is not expedited, and route is priority, change to default
      if (
        !this.quoteDetailsForm.controls.defaultRoute.value ||
        (this.currentSelectedCarrier?.expeditedShipping &&
          this.quoteDetailsForm.controls.defaultRoute.value === route?.id)
      ) {
        // Updated default route when quote has heavy freight
        this.currentSelectedCarrier = selectedCarrier;
        this.resetInitialDefaultRoute();
      }
    }
    this.currentSelectedCarrier = selectedCarrier;
  }

  /**
   * Initializes quote values
   */
  private initializeQuoteValues() {
    if (this.quoteDetailsService.isExistingQuote(this.quoteId)) {
      // load existing quote details
      this.quoteDetailsService.getQuoteDetails(this.quoteId, (quoteDetails: QuoteDetails) => this.initializeExistingQuote(quoteDetails));
    } else {
      // initialize a new quote
      this.initializeNewQuote();
      this.processShipToCompleteValue();
    }
  }

  /**
   * Initializing existing quote details
   * @param quoteDetails existing quote details
   */
  initializeExistingQuote(quoteDetails: QuoteDetails) {
    this.currentAccountManager = quoteDetails.accountManager;
    this.currentCustomer = quoteDetails.customer || quoteDetails.lead;
    this.currentPriceMatrix = quoteDetails.priceMatrix;

    if (this.currentCustomer) {
      this.displayCustomerList =
        this.utilsService.mapCustomersToSelect([this.currentCustomer], this.leadStatusCodeList, this.lnStatusCodeList);
      setTimeout(() => {
        this.quoteDetailsForm.controls.customer.setValue(this.currentCustomer.id);
      }, 0);
    }

    this.quoteDetailsService.checkCreditCardEligibility(
      this.currentCustomer
    );

    if (!quoteDetails.estimatedCloseDate) {
      quoteDetails.estimatedCloseDate = quoteDetails.expireDate;
    }

    if (quoteDetails.isRevision) {
      this.quoteDetailsService.quoteRestored$.next(quoteDetails);
    }

    if (!quoteDetails.isCompleted && this.currentAccountManager) {
      this.quoteDetailsService.setAccountManager$.next(
        this.currentAccountManager.employeeId
      );

      this.quoteDetailsService.getCustomersAndLeads(
        this.currentAccountManager.employeeId, this.hideInactive,
        (customers: Customer[]) => {
          this.customerList = customers;
          this.displayCustomerList = this.utilsService.mapCustomersToSelect(customers, this.leadStatusCodeList, this.lnStatusCodeList);
          if (this.currentCustomer) {
            this.quoteDetailsForm.controls.customer.setValue(this.currentCustomer.id);
          }
          if (quoteDetails.lead && this.priceAsEnabled) {
            this.initPriceAsData(customers, quoteDetails.priceAsCustomerId);
          }
        }
      );
    }
    this.maxDate = quoteDetails.maxExpirationDate;
    this.selectedExpirationDate = quoteDetails.expireDate;
    this.quoteDetailsForm.controls.quoteExpireDate.setValue(
      this.selectedExpirationDate
    );
    this.hasExpirationOption();
    this.updateQuoteHeaderDetails(quoteDetails);
    this.updateQuoteWithCustomerDetails(quoteDetails);
    this.checkIfQuoteIsExpired(quoteDetails);
    this.checkIfQuoteIsMigrated(quoteDetails);
    this.checkIfPriceMatrixChangedAfterMerge(quoteDetails);
    this.quoteDetailsForm.controls.shipToComplete.setValue(quoteDetails.shipToComplete);
    this.processShipToCompleteValue();
    this.quoteDetailsService.isCompleted$.next(quoteDetails.isCompleted);

    this.quoteDetailsService
      .checkIfEditNotificationInProgress(this.quoteId)
      .subscribe(inProgress => {
        if (inProgress) {
          this.editQuoteWithSignalR(null, null);
        }
      });

    const confidenceDefaultValue = quoteDetails.confidenceToClose?.toString() || '1';
    this.quoteDetailsForm.controls.confidenceToClose.patchValue(confidenceDefaultValue);
    this.updateConfidence(confidenceDefaultValue);
  }

  addPublicListPriceMatrixForLead(quoteDetails: QuoteDetails) {
    // if quote is for lead, get lead public list price matrix and add it to price matrix list

    if (this.quoteId) {
      // quote is created, get the public list price matrix
      this.quoteDetailsService.getLeadQuoteDetails(
        quoteDetails.lead.id,
        (defaultDetails: QuoteDetails) => {
          if (defaultDetails.priceMatrix) {
            this.addListPriceInPriceMatrixList(defaultDetails.priceMatrix);
          }
        }
      );
    } else {
      // new quote
      if (quoteDetails.priceMatrix) {
        this.addListPriceInPriceMatrixList(quoteDetails.priceMatrix);
      }
    }
  }

  /**
   * Search for a given price matrix value into price matrix list, and add to the list if not found in it
   * @param priceMatrix - Searched price matrix
   * @returns - found price matrix form price matrix list
   */
  private addListPriceInPriceMatrixList(priceMatrix: PriceMatrix) {
    if (priceMatrix) {
      const publicListPriceMatrix = this.priceMatrixList.find(
        (pMatrix) => pMatrix.priceList === priceMatrix.priceList
      );
      if (!publicListPriceMatrix) {
        this.priceMatrixList.push(priceMatrix);
        this.displayPriceMatrixList = this.utilsService.mapStringValueObjectToSelect(this.priceMatrixList, 'id', 'priceList');
      }
      return publicListPriceMatrix;
    }
  }

  private checkIfQuoteIsExpired(quote: QuoteDetails) {
    const now = new Date().getTime();
    this.quoteDetailsService.checkQuoteIsExpired(quote, now);
    if (quote.quoteIsExpired && !quote.lead && !quote.isCompleted) {
      this.displayQuoteIsExpired.emit(quote);
    }
  }

  private checkIfPriceMatrixChangedAfterMerge(quote) {
    if (quote.requiresPriceMatrixCurrencyChange) {
      this.displayPriceMatrixChangedAfterMerge.emit(quote);
    }
  }

  private checkIfQuoteIsMigrated(migratedQuote) {
    if (migratedQuote.migratedQuoteNumber && !migratedQuote.isConverted) {
      this.handleMigratedQuote.emit(migratedQuote);
    }
  }

  /**
   * Initializes new quote
   */
  private initializeNewQuote() {
    let customerId = this.customerId;
    let leadId = this.leadId;

    if (this.fromPriceAs && this.priceAsService.quoteInfo.customer) {
      if (this.priceAsService.quoteInfo.customer.isLead) {
        leadId = this.priceAsService.quoteInfo.customer.id;
      } else {
        customerId = this.priceAsService.quoteInfo.customer.id;
      }
    }

    if (customerId) {
      this.initializeNewQuoteForCustomer(customerId);
    } else {
      // Create Quote for Lead
      if (leadId) {
        this.initializeNewQuoteForLead(leadId);
      } else {
        this.initializeQuoteWithNoCustomerOrLead();
      }
    }

    this.maxDate = new Date();
    this.maxDate.setDate(this.today.getDate() + 395);
    this.selectedExpirationDate = new Date();
    this.selectedExpirationDate = new Date(
      this.today.setDate(this.today.getDate() + 30)
    );
    this.quoteDetailsForm.controls.quoteExpireDate.setValue(
      this.selectedExpirationDate
    );
    this.hasExpirationOption();
  }

  /**
   * Initialize default values for a quote with no customer or lead selected
   */
  initializeQuoteWithNoCustomerOrLead() {
    if (this.fromPriceAs) {
      this.populateAMCustomerList(this.priceAsService.quoteInfo.accountManager);
    }

    if (!this.isOnline) {
      this.setDefaultAccountManagerForNewQuote();
    }
  }

  /**
   * Initialize new quote for customer
   * @param customerId customer id
   */
  initializeNewQuoteForCustomer(customerId: string) {
    // initialize a new quote with default data for customer
    this.quoteDetailsService.getCustomerQuoteDetails(
      customerId,
      (customerQuoteDetails: QuoteDetails) => {
        this.handleGetCustomerQuoteDetailsCallback(customerQuoteDetails);
        this.quoteDetailsService.checkCreditCardEligibility(
          customerQuoteDetails.customer
        );
      }
    );
    this.quoteDetailsForm.controls.id.markAsDirty();
  }

  /**
   * Initialize new quote for lead
   * @param leadId lead id
   */
  initializeNewQuoteForLead(leadId: string) {
    this.quoteDetailsService.getLeadQuoteDetails(
      leadId,
      (defaultDetails: QuoteDetails) => {
        this.handleGetCustomerQuoteDetailsCallback(defaultDetails);
        this.quoteDetailsService.checkCreditCardEligibility(
          defaultDetails.lead
        );
      }
    );
    this.quoteDetailsForm.controls.id.markAsDirty();
  }

  /**
   * Handles the data received as callback from quoteDetailsService.getCustomerQuoteDetails
   */
  private handleGetCustomerQuoteDetailsCallback(customerDetails: QuoteDetails) {
    if (this.checkIfCustomerQuoteDetails(customerDetails)) {
      this.quoteDetailsService?.currencySign$.next(
        customerDetails.customer?.currencySign
      );
      this.quoteToolService?.atpChecked$.next(customerDetails.atp);
      this.updateQuoteHeaderDetails(customerDetails);
      this.getCustomersAndLeadsAndPopulate(
        this.chooseAccountManagerByCurrentUser(customerDetails).employeeId,
        Boolean(customerDetails.lead)
      );
    }
  }

  /**
   * Checks if the customer has the minimum properties needed to load the quote
   * @param details - the customer details
   * @returns isValid - True, if the customer has minimum properties needed to load the quote, false otherwise
   */
  private checkIfCustomerQuoteDetails(details: QuoteDetails) {
    const isValid = Boolean(
      (details.accountManager || details.externalAccountManager) &&
      (details.customer || details.lead)
    );
    if (!isValid) {
      this.quoteDetailsForm.markAsPristine();
      this.notificationService.showNotification(
        this.appStrings.quote_cannotLoad,
        NotificationType.Error,
        true,
        true,
        () => this.location.back()
      );
    }
    return isValid;
  }

  private getCustomersAndLeadsAndPopulate(amId: string, isLead = false) {
    this.quoteDetailsService.getCustomersAndLeads(amId, this.hideInactive,
      (customerList: Customer[]) => {
        this.customerList = customerList;
        this.displayCustomerList = this.utilsService.mapCustomersToSelect(customerList, this.leadStatusCodeList, this.lnStatusCodeList);
        if (isLead) {
          this.setPriceAsLists(customerList);
        }
      });
  }

  private setCustomers(customers: Customer[]) {
    this.customerList = customers;
    this.displayCustomerList = this.utilsService.mapCustomersToSelect(customers, this.leadStatusCodeList, this.lnStatusCodeList);
    this.disableCustomerField = false;
    this.cdr.detectChanges();
  }

  /**
   * Sets the lists for the Price As autocomplete
   * @param customers the list of retrieved customers for the selected AM
   */
  private setPriceAsLists(customers: Customer[]) {
    if (this.priceAsEnabled && customers?.length) {
      const filteredCustomers = customers.filter((cust) => !cust.isLead);
      this.priceAsList = filteredCustomers;
      this.displayPriceAsList = this.utilsService.mapCustomersToSelect(filteredCustomers, this.leadStatusCodeList, this.lnStatusCodeList);

      // Force update of Price As control to trigger ds-autocomplete refresh
      setTimeout(() => {
        this.quoteDetailsForm.controls.priceAs.updateValueAndValidity();
        this.cdr.detectChanges();
      });
    }
  }

  /**
   * Initializes the lists and model for the Price As autocomplete
   * @param customers the list of retrieved customers for the selected AM
   * @param priceAsCustomerId id of the customer selected in the Price As autocomplete
   */
  private initPriceAsData(customers: Customer[], priceAsCustomerId?: string) {
    this.setPriceAsLists(customers);
    if (priceAsCustomerId) {
      const selectedPriceAs = customers.find(
        (cust) => cust.id === priceAsCustomerId
      );
      if (selectedPriceAs) {
        setTimeout(() => {
          this.currentPriceAs = selectedPriceAs;
          this.quoteDetailsForm.controls.priceAs.setValue(priceAsCustomerId);
          this.quoteToolService.priceMatrixListRefresh$.next(undefined);
        });
      }
    }
  }

  quoteHeaderCallback(quoteDetails: QuoteDetails, calculateTotals?) {
    this.updateCustomer(quoteDetails);
    this.fillDropDownDetails(quoteDetails);
    if (quoteDetails.lead) {
      this.addPublicListPriceMatrixForLead(quoteDetails);
    }
    if (quoteDetails.paymentDetailsId) {
      this.quoteDetailsService.paymentId$.next(quoteDetails.paymentDetailsId);
    } // update the payment details ID
    if (calculateTotals) {
      this.calculateTotals();
    }
  }

  updateLeadHeader(quoteDetails: QuoteDetails, calculateTotals?, forPriceAs?) {
    this.isLead = true;
    this.isFederalCustomer = quoteDetails.lead.isFederal || false;
    if (!forPriceAs) {
      this.quoteDetailsForm.controls.quoteNotes.setValue(
        quoteDetails.quoteNotes
      );
      this.quoteDetailsForm.controls.quoteName.setValue(quoteDetails.quoteName);
    }
    this.filterPriceMatrixes();
    this.quoteHeaderCallback(quoteDetails, calculateTotals);
  }

  updateCustomerHeader(quoteDetails: QuoteDetails, calculateTotals?) {
    this.isLead = false;
    this.isFederalCustomer = quoteDetails.customer.isFederal;

    this.quoteDetailsForm.controls.federalPriorityCode.setValue(quoteDetails.federalPriorityCode?.toString());
    this.quoteDetailsForm.controls.quoteNotes.setValue(quoteDetails.quoteNotes);
    this.quoteDetailsForm.controls.quoteName.setValue(quoteDetails.quoteName);
    this.filterPriceMatrixes();
    this.quoteHeaderCallback(quoteDetails, calculateTotals);
    this.quoteDetailsForm.controls.estimatedCloseDate.patchValue(quoteDetails.estimatedCloseDate);
  }

  /**
   * Prepares customer and dependent fields
   * @param quoteDetails - Quote Details
   * @param shouldUpdateQuoteNo - boolean, if we're creating a new quote or updating an existing one
   * @param calculateTotals - calculate totals for leads
   */
  updateQuoteHeaderDetails(
    quoteDetails: QuoteDetails,
    shouldUpdateQuoteNo = true,
    calculateTotals?
  ) {
    // updates quote number and ID of the parameter is true
    if (shouldUpdateQuoteNo) {
      this.updateQuoteNumber(quoteDetails);
    }

    const amToUse = this.chooseAccountManagerByCurrentUser(quoteDetails);
    this.updateAccountManager(amToUse);
    this.updateCustomerOrLeadHeader(quoteDetails, calculateTotals);
  }

  /**
   * Add default price matrix value into price matrices list, for an existing quote
   * Update Account Manager to Customer's AM if missing
   */
  private updateQuoteWithCustomerDetails(quoteDetails: QuoteDetails) {
    if (quoteDetails.customer && !quoteDetails.isCompleted) {
      this.quoteDetailsService.getCustomerQuoteDetails(
        quoteDetails.customer.id,
        (customerQuoteDetails: QuoteDetails) => {
          const { priceMatrix, customer, atp } = customerQuoteDetails;
          this.addListPriceInPriceMatrixList(priceMatrix);
          this.updateAccountManagerIfMissing(customerQuoteDetails);
          this.quoteDetailsService?.currencySign$.next(customer?.currencySign);
          this.quoteToolService?.atpChecked$.next(atp);
        }
      );
    }
  }

  /**
   * Updates the account manager if missing
   * @param customerDetails - The customer details
   */
  private updateAccountManagerIfMissing(customerDetails) {
    if (this.quoteDetailsForm.controls.accountManager.value) {
      return;
    }
    const amToUse: AccountManager =
      customerDetails.accountManager || customerDetails.externalAccountManager;
    this.updateAccountManager(amToUse);
    this.quoteDetailsForm.markAsDirty();
  }

  private updateCustomerOrLeadHeader(
    quoteDetails: QuoteDetails,
    calculateTotals: any,
    forPriceAs?: boolean
  ) {
    this.updateCanEdit(quoteDetails);
    this.quoteDetailsService.checkCreditCardEligibility(this.currentCustomer);
    if (quoteDetails.lead) {
      // is Lead
      this.updateLeadHeader(quoteDetails, calculateTotals, forPriceAs);
    } else {
      // is Customer
      this.updateCustomerHeader(quoteDetails, calculateTotals);
      // If public list is not the default price matrix for the current quote,
      // it will be excluded from the list and the user won't be able to choose
      // it as a valid option
      if (quoteDetails.priceMatrix?.priceList !== this.publicListConst) {
        this.priceMatrixList = this.priceMatrixList?.filter(
          (item) => item.priceList !== this.publicListConst
        );
      }
    }
  }

  /**
   * Pre-fills quote values
   * @param details - The details
   */
  private fillDropDownDetails(details: QuoteDetails) {
    this.fillDropDownPriceMatrix(details);
    this.fillDropDownDefaultRoute(details);
  }

  private fillDropDownPriceMatrix(details: QuoteDetails) {
    if (details.priceMatrix) {
      const selectedPriceMatrix = this.addListPriceInPriceMatrixList(
        details.priceMatrix
      );
      const priceMatrix = selectedPriceMatrix || details.priceMatrix;

      // emitEvent: false to prevent bad filtering by id in DS component
      this.quoteDetailsForm.controls.priceMatrix.setValue(priceMatrix.id.toString(), { emitEvent: false });
      this.cdr.detectChanges();
    } else {
      this.quoteDetailsForm.controls.priceMatrix.reset();
    }
    this.currentPriceMatrix = details.priceMatrix;
  }

  private fillDropDownDefaultRoute(details: QuoteDetails) {
    if (details.defaultRoute) {
      let selectedRoute = this.defaultRouteList.find(
        (dRoute) => dRoute.route === details.defaultRoute.route
      );
      if (!selectedRoute) {
        this.defaultRouteList.push(details.defaultRoute);
        selectedRoute = details.defaultRoute;
        this.updateDefaultRouteList();
      }

      this.quoteDetailsForm.controls.defaultRoute.setValue(selectedRoute.id);
    }
  }

  /**
   * Method used to enable or disable quote editing
   * @param quoteDetails - the quote
   */
  updateCanEdit(quoteDetails: QuoteDetails) {
    if (typeof quoteDetails.isCompleted === 'undefined') {
      // new quote created
      quoteDetails.isCompleted = false;
    }
    this.quoteDetailsService.isCompleted$.next(quoteDetails.isCompleted);
    this.quoteIsCompleted = quoteDetails.isCompleted;

    if (typeof quoteDetails.canEdit === 'undefined') {
      // new quote
      this.quoteDetailsService.canEdit$.next(true);
    } else {
      this.quoteDetailsService.canEdit$.next(quoteDetails.canEdit);
    }

    if (this.electronAppOutOfSync) {
      this.updateFormEnableStatus(this.electronAppOutOfSync);
      return;
    }
    this.updateFormEnableStatus(this.quoteIsCompleted || this.quoteSaveInProgress);
  }

  private updateFormEnableStatus(status) {
    if (status) {
      this.quoteDetailsForm.controls.customer.disable();
      this.quoteDetailsForm.controls.accountManager.disable();
      this.quoteDetailsForm.controls.priceMatrix.disable();
      this.quoteDetailsForm.controls.priceAs.disable();
      this.quoteDetailsForm.controls.quoteName.disable();
      this.quoteDetailsForm.controls.quoteNotes.disable();
      this.quoteDetailsForm.controls.federalPriorityCode.disable();
      this.quoteDetailsForm.controls.defaultRoute.disable();
      this.quoteDetailsForm.controls.hideInactiveCustomers.disable();
      this.quoteDetailsForm.controls.confidenceToClose.disable();
      this.quoteDetailsForm.controls.estimatedCloseDate.disable();
    } else {
      this.quoteDetailsForm.controls.customer.enable();
      this.quoteDetailsForm.controls.accountManager.enable();
      this.quoteDetailsForm.controls.priceMatrix.enable();
      this.quoteDetailsForm.controls.priceAs.enable();
      this.quoteDetailsForm.controls.quoteName.enable();
      this.quoteDetailsForm.controls.quoteNotes.enable();
      this.quoteDetailsForm.controls.federalPriorityCode.enable();
      this.quoteDetailsForm.controls.defaultRoute.enable();
      this.quoteDetailsForm.controls.hideInactiveCustomers.enable();
      this.quoteDetailsForm.controls.confidenceToClose.enable();
      this.quoteDetailsForm.controls.estimatedCloseDate.enable();
    }
  }

  /**
   * Update Quote Number
   * @param quoteDetails - The quote details
   */
  private updateQuoteNumber(quoteDetails: QuoteDetails) {
    if (quoteDetails.id) {
      this.quoteId = quoteDetails.id;
    }
    this.quoteDetailsForm.controls.quoteNumber.setValue(
      quoteDetails.quoteNumber
    );
    this.quoteDetailsService.quoteNumber$.next(quoteDetails.quoteNumber);
    this.quoteDetailsForm.controls.id.setValue(
      quoteDetails.id || this.appInfoService.emptyGuid
    );
  }

  /**
   * Updates the account manager
   * @param accountManager - The account manager
   */
  updateAccountManager(accountManager: AccountManager) {
    if (!accountManager) {
      return;
    }

    this.quoteDetailsForm.controls.accountManager.setValue(accountManager.employeeId);
    this.currentAccountManager = accountManager;
    // update the oem sku checkbox
    this.quoteToolService.setAccountManagerData(accountManager);

    if (!this.quoteIsCompleted && this.isOnline) {
      this.consignedWarehouseService.getWHList(accountManager.employeeId);
    }
  }

  /**
   * Updates the customer
   * @param quoteDetails - The quote details
   */
  private updateCustomer(quoteDetails: QuoteDetails) {
    this.disableCustomerField = false;

    if (quoteDetails.lead) {
      if (quoteDetails.priceAsCustomer) {
        this.quoteDetailsService?.currencySign$.next(
          quoteDetails.priceAsCustomer?.currencySign
        );
      } else {
        this.quoteDetailsService?.currencySign$.next(
          quoteDetails.lead?.currencySign
        );
      }
      quoteDetails.lead.isLead = true;
      this.currentCustomer = quoteDetails.lead;

      this.quoteDetailsForm.controls.customer.setValue(quoteDetails.lead.id);

      this.quoteDetailsService.setCustomer$.next(quoteDetails.lead);
    } else {
      this.quoteDetailsService?.currencySign$.next(
        quoteDetails?.customer?.currencySign
      );
      quoteDetails.customer.isLead = false;
      this.currentCustomer = quoteDetails.customer;

      this.quoteDetailsForm.controls.customer.setValue(quoteDetails.customer.id);

      this.quoteDetailsService.setCustomer$.next(quoteDetails.customer);
    }

    this.quoteToolService?.atpChecked$.next(quoteDetails.atp);
    if (!this.displayCustomerList.find(c => c.value === this.currentCustomer.id)) {
      this.displayCustomerList =
        this.utilsService.mapCustomersToSelect([this.currentCustomer], this.leadStatusCodeList, this.lnStatusCodeList);
    }
    this.cdr.detectChanges();
  }

  /**
   * Maps federal codes
   * @param templateValues - The template values
   */
  private mapFederalCodes(templateValues: QuoteTemplateValues) {
    return Object.keys(templateValues.FederalPriorityCode).map((key) => ({
      code: Number(key),
      label: templateValues.FederalPriorityCode[key],
    }));
  }

  /**
   * Account Manager selection changed
   * @param event - The event
   */
  selectedAccountManager(event: MatAutocompleteSelectedEvent) {
    const newAccountManager: AccountManager = event.option.value;
    if (!newAccountManager) {
      return;
    }
    if (!this.currentAccountManager) {
      // new quote, no AM
      this.currentAccountManager = event.option.value;
      this.currentCustomer = null;
      this.populateAMCustomerList(newAccountManager);
      this.quoteDetailsService.setAccountManager$.next(
        this.currentAccountManager.employeeId
      );
    } else if (
      this.currentAccountManager.employeeId !== newAccountManager.employeeId
    ) {
      const oldAM = this.currentAccountManager;
      this.disableAccountManagerRestore = true;
      this.currentAccountManager = event.option.value;
      this.currentCustomer = null;
      if (this.hasItemsFromCW) {
        this.displayChangedAMDialog(oldAM);
      } else {
        this.resetCustomerPriceMatrixDefaultRoute();
        this.populateAMCustomerList(newAccountManager);
        this.quoteDetailsService.setAccountManager$.next(
          this.currentAccountManager.employeeId
        );
      }
    }
    if (
      this.currentAccountManager.employeeId === newAccountManager.employeeId &&
      !this.quoteDetailsForm.controls.customer.value
    ) {
      this.setCustomers(this.customerList);
    }
  }

  beforePriceMatrixChanged() {
    const pmValue = this.quoteDetailsForm.value.priceMatrix;
    if (
      pmValue &&
      (typeof pmValue.id !== 'undefined' ||
        typeof pmValue.priceList !== 'undefined')
    ) {
      this.oldPriceMatrix = this.quoteDetailsForm.value.priceMatrix;
    }
  }

  private displayChangedAMDialog(oldAm) {
    const confirmAMChange = () => {
      this.quoteToolService.deleteConsignedProductsFromQuote(
        this.quoteDetailsForm.controls.id.value,
        true
      );
      this.resetCustomerPriceMatrixDefaultRoute();
      this.populateAMCustomerList(this.currentAccountManager);
      this.quoteDetailsService.setAccountManager$.next(
        this.currentAccountManager.employeeId
      );
    };

    const cancelAMChange = () => {
      this.currentAccountManager = oldAm;
      this.quoteDetailsForm.controls.accountManager.setValue(
        this.currentAccountManager
      );
      this.disableCustomerField = false;
    };

    this.dialogTitle = this.appStrings.accountManagerChanged_title;
    this.dialogText = this.appStrings.accountManagerChanged_message;
    this.dialogButtons = [
      { label: this.appStrings.button_confirm, type: 'primary', onclick: confirmAMChange.bind(this) },
      { label: this.appStrings.button_cancel, type: 'secondary', onclick: cancelAMChange.bind(this) },
    ];

    this.cdr.detectChanges();
    this.dialogOpen$.emit(true);
  }

  private resetCustomerPriceMatrixDefaultRoute() {
    this.quoteDetailsForm.controls.customer.reset();
    this.quoteDetailsForm.controls.priceMatrix.reset();
    this.quoteDetailsForm.controls.defaultRoute.reset();
    this.leadId = null;
    this.customerId = null;
  }

  private populateAMCustomerList(accountManager: AccountManager) {
    if (!accountManager) {
      return;
    }
    this.quoteDetailsService.getCustomersAndLeads(
      accountManager.employeeId, this.hideInactive,
      (customers: Customer[]) => {
        this.setCustomers(customers);
        this.displayCustomerList = this.utilsService.mapCustomersToSelect(customers, this.leadStatusCodeList, this.lnStatusCodeList);
        this.quoteDetailsForm.controls.customer.setValue(null);
        this.currentCustomer = null;
        this.cdr.detectChanges();
      }
    );
    this.updateAccountManager(accountManager);
  }

  /**
   * Confirm customer change
   * @param event selected customer
   */
  confirmCustomerChange(event: any) {
    // no confirmation if it's the first change, or if we already had it selected
    const newCustomer: Customer = event.option.value;

    if (!newCustomer) {
      return;
    }

    if (this.leadId === newCustomer.id || this.customerId === newCustomer.id) {
      const pmCtrl = this.quoteDetailsForm.controls.priceMatrix;
      if (!pmCtrl.value) {
        pmCtrl.setValue(this.currentPriceMatrix);
      }
      return;
    }

    if (newCustomer.isLead && this.hasItemsFromCW) {
      this.displayChangedCustomerIntoLeadDialog(newCustomer);
      return;
    }

    if (!this.productCount ||
      (
        !this.leadId &&
        !this.customerId &&
        !(this.quoteDetailsForm.controls.customer.value)
      )
    ) {
      this.selectedCustomer(newCustomer);
      return;
    }

    this.dialogTitle = this.appStrings.quote_detailsConfirmTitle;
    this.dialogText = this.appStrings.quote_detailsCustomerMessage;
    const changeCustomer = () => this.customerChangeConfirmationResponse(0, newCustomer);
    const keepCustomer = () => this.customerChangeConfirmationResponse(1, newCustomer);
    this.dialogButtons = [
      { label: 'Yes', type: 'primary', onclick: changeCustomer.bind(this) },
      { label: 'No', type: 'secondary', onclick: keepCustomer.bind(this) },
    ];

    this.cdr.detectChanges();
    this.dialogOpen$.emit(true);
  }

  /**
   * Customer change confirmation button pressed
   * @param result pressed button
   * @param newCustomer new selected customer
   */
  customerChangeConfirmationResponse(result: number, newCustomer: Customer) {
    // yes button pressed, else revert
    if (result === 0) {
      this.quoteDetailsService.priceMatrixOrCustomerChangedNotification$.next(
        true
      );
      this.selectedCustomer(newCustomer);
    } else {
      const oldCustomerId = this.getOldCustomerId();

      this.revertCustomer(oldCustomerId);
    }
  }

  /**
   * Getting customer id
   */
  getOldCustomerId() {
    if (this.currentCustomer && this.currentCustomer.id) {
      return this.currentCustomer.id;
    } else if (this.customerId) {
      return this.customerId;
    } else {
      return this.leadId;
    }
  }

  /**
   * Display a dialog to confirm customer change in lead if the quote has items from CW
   * @param lead - the selected lead
   */
  displayChangedCustomerIntoLeadDialog(lead: Customer) {
    const confirmLeadChange = () => {
      this.quoteToolService.deleteConsignedProductsFromQuote(
        this.quoteDetailsForm.controls.id.value,
        false,
        () => this.selectedCustomer(lead)
      );
    };

    const cancelLeadChange = () => {
      const oldCustomerId = this.customerId ? this.customerId : this.leadId;
      this.revertCustomer(oldCustomerId);
    };

    this.dialogTitle = this.appStrings.customerChanged_title;
    this.dialogText = this.appStrings.customerChanged_message;
    this.dialogButtons = [
      { label: this.appStrings.button_confirm, type: 'primary', onclick: confirmLeadChange.bind(this) },
      { label: this.appStrings.button_cancel, type: 'secondary', onclick: cancelLeadChange.bind(this) },
    ];

    this.cdr.detectChanges();
    this.dialogOpen$.emit(true);
  }

  /**
   * Customer selection changed
   * @param newCustomer - The new customer object
   */
  private selectedCustomer(newCustomer: Customer) {
    let customerType: string;

    this.currentCustomer = newCustomer;
    this.isFederalCustomer = newCustomer.isFederal;
    this.isLead = newCustomer.isLead;
    this.filterPriceMatrixes();

    if (newCustomer.isLead) {
      if (this.leadId !== newCustomer.id && this.currentPriceAs) {
        this.currentPriceAs = null;
        this.quoteDetailsForm.controls.priceAs.setValue(null);
      }
      this.leadId = newCustomer.id;
      this.customerId = null;
      customerType = CustomerType.Lead;
      this.setPriceAsLists(this.customerList);
    } else {
      this.leadId = null;
      this.customerId = newCustomer.id;
      this.currentPriceAs = null;
      this.quoteDetailsForm.controls.priceAs.setValue(null);
      customerType = CustomerType.Customer;
    }

    this.paymentDetailsService.resetCallsDoneFlag();

    this.quoteDetailsService.updateCustomer$.next(newCustomer);
    if (!newCustomer.isLead) {
      this.quoteDetailsService.setAccountManager$.next(
        this.currentAccountManager?.employeeId
      );
    }
    this.quoteDetailsService.getQuoteDetailsByClientType(
      customerType,
      newCustomer.id,
      // update quote customer or lead header
      (defaultDetails: QuoteDetails) => {
        this.updateCustomerOrLeadHeader(defaultDetails, true);
      }
    );
  }

  /**
   * Handles the selection of a value from the Price As autocomplete
   * @param customer selected Customer
   */
  selectPriceAs(customer: Customer) {
    if (!customer) {
      if (this.currentPriceAs) {
        const priceAsField = this.quoteDetailsForm.controls.priceAs;
        priceAsField.setValue(this.currentPriceAs);
      }
      return;
    }
    this.currentPriceAs = customer;

    this.paymentDetailsService.resetCallsDoneFlag();

    this.quoteDetailsService.getQuoteDetailsByClientType(
      CustomerType.Customer,
      customer.id,
      // update quote customer or lead header
      (defaultDetails: QuoteDetails) => {
        defaultDetails.lead = this.currentCustomer as Lead;
        defaultDetails.lead.currencySign = defaultDetails.customer.currencySign;
        defaultDetails.customer = null;
        this.updateCustomerOrLeadHeader(defaultDetails, true, true);
      }
    );
  }

  revertCustomer(oldCustomerId) {
    const oldCustomer = this.customerList.find(
      (cust) => cust.id === oldCustomerId
    );
    if (!oldCustomer) {
      this.quoteDetailsForm.controls.customer.setValue(null);
      return;
    }
    this.quoteDetailsForm.controls.customer.setValue(oldCustomer.id);
  }

  /**
   * Price matrix was changed
   */
  priceMatrixChanged() {
    const confirmPriceMatrixChange = () => {
      const pMatrixCtrl = this.quoteDetailsForm.controls.priceMatrix;
      this.currentPriceMatrix = this.priceMatrixList.find(pm => pm.id.toString() === pMatrixCtrl.value);
      this.quoteDetailsService.priceMatrixOrCustomerChangedNotification$.next(true);
      this.filterPriceMatrixes();
      if (this.currentPriceAs) {
        if (
          pMatrixCtrl.value?.priceList === this.industrialListConst ||
          pMatrixCtrl.value?.priceList === this.publicListConst
        ) {
          this.clearPriceAsAndRecalculatePrices();
          return;
        }
      }
      this.calculateTotals();
    };

    const revertPriceMatrixChange = () => {
      if (this.oldPriceMatrix) {
        this.quoteDetailsForm.controls.priceMatrix.setValue(this.oldPriceMatrix.id.toString());
      } else {
        this.quoteDetailsForm.controls.priceMatrix.setValue(null);
      }
      this.quoteDetailsForm.controls.priceMatrix.markAsPristine();
    };

    this.dialogTitle = this.appStrings.quote_detailsConfirmTitle;
    this.dialogText = this.appStrings.quote_detailsPriceMatrixMessage;
    this.dialogButtons = [
      { label: 'Yes', type: 'primary', onclick: confirmPriceMatrixChange.bind(this) },
      { label: 'No', type: 'secondary', onclick: revertPriceMatrixChange.bind(this) },
    ];

    this.cdr.detectChanges();
    this.dialogOpen$.emit(true);
  }

  private calculateTotals() {
    if (this.quoteId) {
      this.editQuoteWithSignalR(
        this.mapValuesForCalculateTotals(this.getQuoteDetails()),
        true
      );
    }
  }

  private editQuoteWithSignalR(qDetails, ignoreCompleteAllCallback) {
    this.quoteDetailsService.quoteIsSaving$.next(true);
    this.quoteDetailsForm.markAsPristine();
    this.signalrService
      .startConnection(SignalrConnectionType.EditQuoteForCalculateTotals, true)
      .then(() => {
        if (qDetails) {
          // the quote edit is intended
          qDetails.notification = new SignalrNotification(
            null,
            this.appInfoService.userData.UserInfo.EmployeeId,
            SignalrMethodName.editQuote
          );
          this.quoteDetailsService.editQuote(
            qDetails,
            () => this.quoteToolService.checkForResetQuoteRestored$.next(true),
            () => {
              this.quoteDetailsService.quoteIsSaving$.next(false);
            },
            ignoreCompleteAllCallback
          );
        }
      });
  }

  private actionAfterQuoteEdit(entityId?) {
    this.signalrService.closeConnection(() => {
      if (entityId && entityId === this.quoteId) {
        // the edit is done for current quote
        this.quoteDetailsService.quoteIsSaving$.next(true);
        this.quoteDetailsService.refreshCarriers$.next(undefined);
        this.quoteToolService.refreshToolsList$.next(() => {
          this.quoteDetailsService.quoteIsSaving$.next(false);
          this.paymentDetailsService.calculatePaymentSummary(this.quoteId);
        });
      } else {
        // check again if edit in progress, connect to hub
        this.quoteDetailsService
          .checkIfEditNotificationInProgress(this.quoteId)
          .subscribe((inProgress) => {
            if (inProgress) {
              this.editQuoteWithSignalR(null, null);
            } else {
              this.quoteDetailsService.quoteIsSaving$.next(false);
            }
          });
      }
    });
  }

  private mapValuesForCalculateTotals(quoteDetails) {
    const payload: any = {};
    payload.id = this.quoteId;
    if (quoteDetails.customer.isLead) {
      payload.leadId = quoteDetails.customer.id;
      payload.customerId = null;
    } else {
      payload.customerId = quoteDetails.customer.id;
      payload.leadId = null;
    }
    payload.priceAsCustomerId = this.currentPriceAs
      ? this.currentPriceAs.id
      : null;
    payload.federalPriorityCode = quoteDetails.federalPriorityCode;
    payload.priceMatrix = quoteDetails.priceMatrix;
    payload.defaultRoute = quoteDetails.defaultRoute;
    payload.quoteNotes = quoteDetails.quoteNotes;
    payload.quoteName = quoteDetails.quoteName;
    payload.expireDate = quoteDetails.quoteExpireDate;
    payload.createdForEmployeeId = this.currentAccountManager?.employeeId;
    payload.atp = quoteDetails.atp;
    payload.hideInactiveCustomers = quoteDetails.hideInactiveCustomers;

    return payload;
  }

  /**
   * Returns quote details
   */
  getQuoteDetails() {
    const formValue = this.quoteDetailsForm.getRawValue();

    formValue.customer = this.currentCustomer;
    formValue.accountManager = this.currentAccountManager;
    formValue.priceMatrix = this.currentPriceMatrix;
    formValue.confidenceToClose = this.confidenceOptions[formValue.confidenceToClose];

    const priceAsCustomer = this.priceAsList.find(customer => customer.id === formValue.priceAs);
    if (priceAsCustomer) {
      formValue.priceAs = priceAsCustomer;
    }

    const defaultRoute = this.defaultRouteList.find(route => route.id === formValue.defaultRoute);
    if (defaultRoute) {
      formValue.defaultRoute = defaultRoute;
    }

    const federalCode = this.federalPriorityCodeList.find(code => code.code.toString() === formValue.federalPriorityCode);
    if (federalCode) {
      formValue.federalPriorityCode = federalCode.code;
    }

    return formValue;
  }

  /**
   * Sets new quote details on the form
   * @param newQuoteDetails - The new quote details
   */
  updateQuoteDetailsAfterSave(newQuoteDetails: QuoteDetails) {
    this.fillDropDownDetails(newQuoteDetails);
    this.updateQuoteNumber(newQuoteDetails);
    this.updateCanEdit(newQuoteDetails);
    // update the quoteId in the service
    if (newQuoteDetails.id && newQuoteDetails.id.length > 0) {
      this.quoteToolService.setQuoteId(newQuoteDetails.id);
    }
    this.quoteDetailsForm.markAsPristine();
  }

  /**
   * Tells if the form has changes
   */
  hasChanges() {
    return this.quoteDetailsForm.dirty;
  }

  /**
   * Determines if the form is valid
   */
  isValid() {
    this.quoteDetailsForm.controls.customer.markAsTouched();
    this.quoteDetailsForm.controls.accountManager.markAsTouched();
    this.quoteDetailsForm.controls.priceMatrix.markAsTouched();

    const priceMatrixValue = this.quoteDetailsForm.controls.priceMatrix.value;
    const isValidPriceMatrix = this.displayPriceMatrixList?.some(pm => pm.value === priceMatrixValue) ||
      this.currentPriceMatrix;
    if (!isValidPriceMatrix) {
      this.quoteDetailsForm.controls.priceMatrix.setErrors({
        invalidValue: true,
      });
      return false;
    }

    const hasValidPriceMatrix = priceMatrixValue && this.currentPriceMatrix;

    return this.quoteDetailsForm.value.customer && hasValidPriceMatrix;
  }

  private setEditQuoteProgress(progress) {
    if (progress.state === SignalrProgressState.Done) {
      this.actionAfterQuoteEdit(progress.message.entityId);
    }
    if (progress.state === SignalrProgressState.Error) {
      this.quoteDetailsService.quoteIsSaving$.next(false);
      this.signalrService.closeConnection();
      this.notificationService.showNotification(
        progress.message,
        NotificationType.Error,
        true
      );
    }
  }

  private updateStatusForQuoteSavingProgress(progress) {
    this.quoteSaveInProgress = progress;
    if (this.quoteSaveNotificationTimeout) {
      clearTimeout(this.quoteSaveNotificationTimeout);
    }
    if (progress) {
      // delay display of notification, so that the user does not see it for short save times
      this.quoteSaveNotificationTimeout = setTimeout(
        () => this.updateStatusForQuoteSavingProgressHandle(progress),
        this.delayForNotification
      );
    } else {
      this.updateStatusForQuoteSavingProgressHandle(progress);
    }
  }

  private updateStatusForQuoteSavingProgressHandle(progress) {
    if (progress) {
      this.isNotificationShown = true;
      this.notificationService.showNotification(
        this.appStrings.quoteEdit_pleaseWait,
        NotificationType.Warning,
        true,
        true
      );
    } else if (this.isNotificationShown) {
      this.isNotificationShown = false;
      this.notificationService.dismissNotification();
    }
    this.updateFormEnableStatus(progress);
  }

  /**
   * Sets the current user as default Account Manager when creating a new quote from quotes page
   */
  private setDefaultAccountManagerForNewQuote() {
    this.currentAccountManager = this.accountManagerList[0];
    this.populateAMCustomerList(this.currentAccountManager);
  }

  private chooseAccountManagerByCurrentUser(details) {
    let accountManager: AccountManager = null;
    if (
      details.externalAccountManager &&
      (details.externalAccountManager.employeeId ===
        this.appInfoService.userData.UserInfo.EmployeeId ||
        !details.accountManager)
    ) {
      accountManager = details.externalAccountManager;
    } else {
      accountManager = details.accountManager;
    }

    this.quoteDetailsService.setAccountManager$.next(accountManager.employeeId);
    return accountManager;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Resets the Price As field to null and recalculates prices based on industrial/public list
   */
  private clearPriceAsAndRecalculatePrices() {
    this.quoteDetailsForm.controls.priceAs.setValue(null);
    this.currentPriceAs = null;

    this.quoteDetailsService.getQuoteDetailsByClientType(
      CustomerType.Lead,
      this.currentCustomer.id,
      // update quote customer or lead header
      (defaultDetails: QuoteDetails) =>
        this.updateCustomerOrLeadHeader(defaultDetails, true, true)
    );
  }

  /**
   * Emits hideInactiveChange value
   * Reloads customers dropdown list when needed
   * @param newValue checkbox value
   */
  hideInactiveCustomersToggle(newValue?: boolean) {
    const value = newValue ?? this.quoteDetailsForm.controls.hideInactiveCustomers.value;
    this.hideInactiveChange.emit(value);
    this.hideInactive = value;
    this.quoteDetailsForm.controls.hideInactiveCustomers.patchValue(this.hideInactive);
    if (this.currentAccountManager) {
      this.quoteDetailsService.getCustomersAndLeads(
        this.currentAccountManager.employeeId, value,
        (customers: Customer[]) => {
          this.setCustomers(customers);
          const currentCustomerValue = this.quoteDetailsForm.controls.customer.value;
          this.quoteDetailsForm.controls.customer.setValue(null);
          if (currentCustomerValue) {
            this.quoteDetailsForm.controls.customer.setValue(currentCustomerValue);
          }
        }
      );
    }
  }

  /**
   * Handle account manager selection
   * @param event - The select event from ds-autocomplete
   */
  onAccountManagerSelected(event: any) {
    if (event.option.value && this.accountManagerList) {
      const selectedManager = this.accountManagerList.find(manager => manager.employeeId === event.option.value);
      if (selectedManager) {
        this.selectedAccountManager({ option: { value: selectedManager } } as MatAutocompleteSelectedEvent);
      }
    }
  }

  /**
   * Handle customer selection
   * @param event - The select event from ds-autocomplete
   */
  onCustomerSelected(event: any) {
    if (event.option.value && this.customerList) {
      const selectedCustomer = this.customerList.find(customer => customer.id === event.option.value);
      if (selectedCustomer) {
        this.confirmCustomerChange({ option: { value: selectedCustomer } } as MatAutocompleteSelectedEvent);
      }
    }
  }

  /**
   * Handle price as selection
   * @param event - The select event from ds-autocomplete
   */
  onPriceAsSelected(event: any) {
    if (event.option.value && this.priceAsList) {
      const selectedPriceAs = this.priceAsList.find(customer => customer.id === event.option.value);
      if (selectedPriceAs) {
        this.selectPriceAs(selectedPriceAs);
      }
    }
  }

  /**
   * Resets price matrix to the default value for the current lead
   */
  private resetPriceMatrixToDefault() {
    if (this.currentCustomer?.isLead) {
      this.quoteDetailsService.getLeadQuoteDetails(
        this.currentCustomer.id,
        (defaultDetails: QuoteDetails) => {
          if (defaultDetails.priceMatrix) {
            this.addListPriceInPriceMatrixList(defaultDetails.priceMatrix);
            this.fillDropDownPriceMatrix(defaultDetails);
            this.calculateTotals();
          }
        }
      );
    }
  }

  /**
   * Handle price matrix selection
   * @param event - The select event from ds-autocomplete
   */
  onPriceMatrixSelected(event: any) {
    if (event.option.value && this.priceMatrixList) {
      const selectedPriceMatrix = this.priceMatrixList.find(pm => pm.id === parseInt(event.option.value, 10));
      if (selectedPriceMatrix) {
        this.oldPriceMatrix = this.currentPriceMatrix;
        this.priceMatrixChanged();
      }
    }
  }
}
